/**
 * /you — your profile on a page of its own, without the feed (the founder's ruling, 27 Sep:
 * opening your profile should not load the feed first).
 *
 * Mounted by src/index.tsx like /join and /stats: no Carousel, no TelemetryTransport, no feed
 * resource, no window.__blitz. Opening it never starts a card session. It shows what the account
 * sheet's profile view shows — the handle (and the one quiet way to change it), the email and
 * the count, *Your cards* with where each stands and Take down, *Liked*, the numbers link for
 * an admin — the way to your public page, and the ways on: the top bar's Home, Catalogue and
 * Feed (PageNav.tsx, 27 Sep: the page used to offer only the feed) and adding something. Who is here is
 * PEEKED on load, never minted, as on /join. This file imports nothing that can emit a row;
 * tests/unit/join.test.ts pins that list. The build with no backend honours
 * window.__blitzFixture.account, as the bench honours .admin, so the e2e suite can be someone.
 */
import { Match, Show, Switch, createSignal, onMount } from 'solid-js';
import { ANONYMOUS, createAccountApi, validateHandle, type Account, type AccountClient } from '../api/account';
import { resetSession, supabase } from '../backend/supabase';
import { feedDoor } from '../lib/join';
import { MakerPanel } from './MakerPanel';
import { PageNav } from './PageNav';

/** The session as it is, never minted. */
const peekSession = async () => (supabase ? (await supabase.auth.getSession()).data.session : null);
const peek = createAccountApi(supabase as unknown as AccountClient | null, peekSession, resetSession);

export type ProfileView = 'loading' | 'stranger' | 'handle' | 'profile';
export const profileViewFor = (a: Account): ProfileView => (a.isAccount ? (a.handle ? 'profile' : 'handle') : 'stranger');

export function ProfilePage() {
  const search = location.search;
  const [acct, setAcct] = createSignal<Account>(ANONYMOUS);
  const [view, setView] = createSignal<ProfileView>('loading');
  const [handle, setHandle] = createSignal('');
  const [changing, setChanging] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [note, setNote] = createSignal<string | null>(null);

  const show = (a: Account) => {
    setAcct(a);
    setView(profileViewFor(a));
    document.title = `${a.handle ? `@${a.handle}` : 'Your profile'} — Blitz`;
  };
  onMount(async () => {
    const fixture = !supabase ? (window as unknown as { __blitzFixture?: { account?: Partial<Account> } }).__blitzFixture?.account : undefined;
    show(fixture ? { ...ANONYMOUS, ...fixture } : await peek.load());
  });

  const saveHandle = (e: Event) => {
    e.preventDefault();
    const v = validateHandle(handle());
    if ('error' in v) {
      setError(v.error);
      return;
    }
    setBusy(true);
    setError(null);
    void peek.claim(v.handle).then((r) => {
      setBusy(false);
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      show({ ...acct(), handle: r.handle });
      setNote(`You are @${r.handle}.`);
      setHandle('');
      setChanging(false);
    });
  };
  const signOut = () => {
    setBusy(true);
    void peek.signOut().then((a) => {
      setBusy(false);
      setNote(null);
      show(a);
    });
  };
  /** A card of yours or one you liked: the feed, positioned on it, tagged as a profile arrival. */
  const goTo = (id: string) => location.assign(`/c/${encodeURIComponent(id)}?via=profile`);

  return (
    <div class="app">
      <PageNav current="you" feed={feedDoor(search)} signin="/signin" isAccount={acct().isAccount} handle={acct().handle} feedTestId="profile-feed-top" whoTestId="profile-who" />

      <main class="join-page profile-page" data-testid="profile-page" data-view={view()}>
        <section class="link-sheet join-card">
          <Switch>
            <Match when={view() === 'loading'}>
              <p class="hint">Reading who this is…</p>
            </Match>
            <Match when={view() === 'stranger'}>
              <div class="join-body" data-testid="profile-stranger">
                <h1>Your profile</h1>
                <p class="hint">Sign in to see your cards and what you liked. Looking around never needs an account.</p>
                <div class="row">
                  <a class="btn" href="/signin" data-testid="profile-signin">
                    Sign in
                  </a>
                  <a class="btn primary" href="/join" data-testid="profile-join">
                    Create your account
                  </a>
                </div>
              </div>
            </Match>
            <Match when={view() === 'handle' || view() === 'profile'}>
              <div class="join-body" data-testid="profile-body">
                <h1 data-testid="profile-handle">{acct().handle ? `@${acct().handle}` : 'Pick your handle'}</h1>
                <p class="hint" data-testid="profile-line">
                  <Show when={acct().email}>{acct().email} · </Show>
                  {acct().cardCount === 1 ? '1 card linked' : `${acct().cardCount} cards linked`}
                </p>
                <Show when={acct().handle}>
                  <p class="hint links">
                    <a class="linkish quiet" href={`/@${acct().handle}`} data-testid="profile-public">
                      Your public page: blitzfeed.app/@{acct().handle}
                    </a>
                  </p>
                </Show>
                <Show when={note()}>
                  <p class="hint" role="status" data-testid="profile-note">
                    {note()}
                  </p>
                </Show>
                {/* The handle is the name on everything linked; changing it is possible but not
                    something the page invites. It sits behind one quiet link and a 30-day rule. */}
                <Show
                  when={!acct().handle || changing()}
                  fallback={
                    <p class="hint links">
                      <button type="button" class="linkish" onClick={() => setChanging(true)} data-testid="acct-change-handle">
                        Change handle
                      </button>
                      <span style="color:var(--fg-faint)"> · once every 30 days</span>
                    </p>
                  }
                >
                  <form onSubmit={saveHandle} novalidate>
                    <label>
                      <span>{acct().handle ? 'New handle' : 'Handle'}</span>
                      <div class="at">
                        <b>@</b>
                        <input type="text" autocomplete="nickname" autocapitalize="off" spellcheck={false} placeholder={acct().handle ?? 'yourname'} value={handle()} onInput={(e) => setHandle(e.currentTarget.value)} data-testid="acct-handle" />
                      </div>
                    </label>
                    <Show when={error()}>
                      <p class="error" role="alert" data-testid="acct-error">
                        {error()}
                      </p>
                    </Show>
                    <div class="row">
                      <Show when={acct().handle}>
                        <button type="button" class="btn" onClick={() => { setChanging(false); setHandle(''); setError(null); }}>
                          Keep it
                        </button>
                      </Show>
                      <button type="submit" class="btn primary" disabled={busy() || !handle().trim()} data-testid="acct-save-handle">
                        {acct().handle ? 'Change' : 'Claim handle'}
                      </button>
                    </div>
                  </form>
                </Show>
                <Show when={acct().handle}>
                  <MakerPanel onGoTo={goTo} onRemoved={() => void (supabase && peek.load().then(show))} toast={setNote} />
                </Show>
                <Show when={acct().isAdmin}>
                  <p class="hint">
                    <a class="linkish" href="/stats" data-testid="stats-link">
                      All the numbers, on a page: /stats
                    </a>
                  </p>
                </Show>
                <div class="row" style="margin-top:14px">
                  <a class="btn primary" href={feedDoor(search, { link: '1' })} data-testid="profile-add">
                    Add what you made
                  </a>
                  <button type="button" class="btn" onClick={signOut} disabled={busy()} data-testid="acct-signout">
                    Sign out
                  </button>
                </div>
              </div>
            </Match>
          </Switch>
        </section>
      </main>
    </div>
  );
}
